import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { useMatch } from 'react-router-dom';

import { alignmentsAtom } from '@/atoms/alignment.atom';
import { crestSourcesAtom } from '@/atoms/crestLine.atom';
import { layersAtom } from '@/atoms/layers.atom';

import type { AnalysisProjectContext } from './types';

/**
 * What the registry's `acceptsContext` / `prerequisite` need to know about
 * the open project. `null` until the route's project id is known — every
 * analysis resolves to hidden then.
 *
 * `crestSourcesAtom` is a `Map<layerId, CrestSource>`; the contract only needs
 * the id set, derived once per registry change.
 */
export function useAnalysisProjectContext(): AnalysisProjectContext | null {
  // Called from inside dialogs, which render above <Routes> where useParams() is {}.
  const projectId = useMatch('/projects/:projectId/*')?.params.projectId;
  const layers = useAtomValue(layersAtom);
  const crestById = useAtomValue(crestSourcesAtom);
  const alignments = useAtomValue(alignmentsAtom);
  const crestSources = useMemo<ReadonlySet<string>>(() => new Set(crestById.keys()), [crestById]);
  const hasAlignments = alignments.size > 0;
  return useMemo(
    () => (projectId ? { projectId, layers, crestSources, hasAlignments } : null),
    [projectId, layers, crestSources, hasAlignments],
  );
}
